import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { listProducts, listCategories } from "@/lib/api";
import { useT } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { useSiteContent } from "@/context/SiteContentContext";

/**
 * CatalogContext — fetches products once on mount and exposes:
 *  - products: full list (or [] while loading / on error)
 *  - loading: boolean
 *  - availableCategories: only category slugs that have ≥1 product, in canonical
 *    admin-defined order (db.categories), each with { slug, to, label, count, image }
 *  - randomProductImages(n): n unique random images sourced from real products
 *
 * Consumed by Home, Header (burger menu) and Footer so a category with zero
 * inventory disappears from every entry point automatically.
 */

const CatalogCtx = createContext({
  products: [],
  loading: true,
  availableCategories: [],
  categories: [],
  refreshCategories: () => {},
  randomProductImages: () => [],
});

export const CatalogProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rawCategories, setRawCategories] = useState([]);
  const { settings } = useSiteContent();
  const { lang } = useT();
  const { syncPrices } = useCart();
  useEffect(() => { if (products.length) syncPrices(products); }, [products, syncPrices]);

  const refreshCategories = useCallback(
    () => listCategories().then((c) => setRawCategories(Array.isArray(c) ? c : [])).catch(() => {}),
    []
  );
  useEffect(() => { refreshCategories(); }, [refreshCategories]);

  useEffect(() => {
    let mounted = true;
    listProducts()
      .then((p) => mounted && setProducts(Array.isArray(p) ? p : []))
      .catch(() => mounted && setProducts([]))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const categoryImageOverrides = settings?.category_images || {};

  const value = useMemo(() => {
    // Aggregate by category, but only count products that have at least
    // one variant in stock. A category with only sold-out items would
    // land the shopper on an empty grid, so we hide it from every entry
    // point (Header dropdown, Home tiles, Footer) until stock returns.
    const byCat = new Map();
    for (const p of products) {
      const inStock = (p.variants || []).some((v) => (v.stock ?? 0) > 0);
      if (!inStock) continue;
      const list = byCat.get(p.category) || [];
      list.push(p);
      byCat.set(p.category, list);
    }

    const categories = rawCategories.map((c) => ({
      ...c,
      label: c[`name_${lang}`] || c.name_en || c.name_pt || c.slug,
    }));
    const availableCategories = categories.filter((c) => c.visible !== false && byCat.has(c.slug)).map(
      ({ slug, label }) => {
        const items = byCat.get(slug) || [];
        const firstWithImg = items.find((p) => p.images?.[0]);
        // Admin can override the tile image per category via
        // /admin → Content → Category tiles. Falls back to the first
        // product photo in that category.
        const override = categoryImageOverrides[slug];
        return {
          slug,
          to: `/collection/${slug}`,
          label,
          count: items.length,
          image: override || firstWithImg?.images?.[0] || null,
        };
      }
    );

    // Collect all product images, dedupe, shuffle deterministically-per-mount
    const allImages = Array.from(
      new Set(products.flatMap((p) => p.images || []).filter(Boolean))
    );
    const shuffled = [...allImages].sort(() => Math.random() - 0.5);
    const randomProductImages = (n) => shuffled.slice(0, n);

    return { products, loading, availableCategories, categories, refreshCategories, randomProductImages };
  }, [products, loading, categoryImageOverrides, rawCategories, lang, refreshCategories]);

  return <CatalogCtx.Provider value={value}>{children}</CatalogCtx.Provider>;
};

export const useCatalog = () => useContext(CatalogCtx);
